class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      '[data-predictive-search]',
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    /* HCS (18 Aug 2026, Finbar: "it seems to function weirdly in general as a
       search box when typing").
       Two Dawn defects fixed here.
       1. close(true) BLANKS the input (see closeResults). Running it from
          keyup meant any keystroke that left the TRIMMED value empty wiped
          what the shopper had typed - a leading space was enough to do it.
          The dropdown is now closed without touching the value.
       2. The unconditional preventDefault() ran on every key release,
          including during dictation and IME composition, where it interferes
          with the keyboard's own text handling. Dawn only ever needed it for
          the arrow keys, and onKeydown already blocks those. */
    if (!this.getQuery().length) this.close();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      '[data-predictive-search-search-for-text]',
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll('li, button.predictive-search__item'),
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]',
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    /* HCS UI-4 (26 Jul 2026): products only — no query/collection/page
       suggestions. Restricting the request (rather than only hiding the markup)
       keeps the payload small; sections/predictive-search.liquid also suppresses
       those groups as a belt-and-braces guard. */
    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        /* HCS 23 Oct 2026: inch/mm and alias widening, assets/hcs-search-alias.js */
        window.hcsSearchAlias ? window.hcsSearchAlias(searchTerm) : searchTerm,
      )}&section_id=predictive-search&resources[type]=product&resources[limit]=5`,
      { signal: this.abortController.signal },
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const shopifyMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        return this.hcsTopUp(shopifyMarkup, searchTerm);
      })
      .then((resultsMarkup) => {
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          },
        );
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  /* HCS (12 Oct 2026, Finbar: "why is the search dropdown suggestion giving so
     few alorair results"). Shopify's predictive search under-returns on a single
     rare word - `alorair` gave 1 of 5 AlorAir products, `slgr` 1 of 2 - and the
     theme cannot change that engine. When it returns fewer than five products,
     top the list up from the full search through sections/hcs-search-fill.liquid
     (every word of the query must be in the title, type, tags or vendor), after
     Shopify's own and without repeats. Any failure falls back to Shopify's
     markup untouched - the top-up can only ever add. */
  hcsTopUp(markup, searchTerm) {
    const HCS_MAX = 5;
    const doc = new DOMParser().parseFromString(
      `<div id="hcs-ps-root">${markup}</div>`,
      'text/html',
    );
    const root = doc.getElementById('hcs-ps-root');
    const list = root.querySelector('#predictive-search-results-products-list');
    const have = list ? list.querySelectorAll(':scope > li').length : 0;
    if (have >= HCS_MAX) return Promise.resolve(markup);

    const url =
      `${routes.predictive_search_url.replace(/\/suggest$/, '')}?q=${encodeURIComponent(window.hcsSearchAlias ? window.hcsSearchAlias(searchTerm) : searchTerm)}` +
      '&type=product&options%5Bprefix%5D=last&section_id=hcs-search-fill';
    return fetch(url, { signal: this.abortController.signal })
      .then((response) => (response.ok ? response.text() : ''))
      .then((text) => {
        if (!text) return markup;
        const fill = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelectorAll('[data-hcs-search-fill] > li');
        if (!fill.length) return markup;

        const pathOf = (a) => (a ? new URL(a.getAttribute('href'), location.origin).pathname : '');
        const shown = new Set(
          list ? [...list.querySelectorAll('a[href]')].map(pathOf) : [],
        );
        let ul = list;
        if (!ul) {
          // Shopify returned no products at all: build the products group
          // exactly as sections/predictive-search.liquid would have.
          const resultsEl = root.querySelector('#predictive-search-results');
          if (!resultsEl) return markup;
          const wrap = doc.createElement('div');
          wrap.id = 'predictive-search-results-groups-wrapper';
          wrap.className =
            'predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions';
          wrap.innerHTML =
            '<div class="predictive-search__result-group"><div>' +
            '<h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2>' +
            '<ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products"></ul>' +
            '</div></div>';
          resultsEl.insertBefore(wrap, resultsEl.firstChild);
          ul = wrap.querySelector('ul');
        }
        let count = have;
        fill.forEach((li) => {
          if (count >= HCS_MAX) return;
          const path = pathOf(li.querySelector('a[href]'));
          if (!path || shown.has(path)) return;
          shown.add(path);
          ul.appendChild(doc.importNode(li, true));
          count += 1;
        });
        // Keep the option ids sequential for keyboard navigation.
        ul.querySelectorAll(':scope > li').forEach((li, i) => {
          li.id = `predictive-search-option-product-${i + 1}`;
        });
        return root.innerHTML;
      })
      .catch((error) => {
        if (error?.name === 'AbortError' || error?.code === 20) throw error;
        return markup;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText =
      this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(
      this.querySelector('[data-predictive-search-live-region-count-value]')
        .textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      /* HCS (18 Aug 2026): Dawn also did `this.input.value = ''` here. Every
         caller reaches this from a keystroke or a debounced input event, so it
         could delete text the shopper had just typed. Clearing the field is the
         reset button's job and SearchForm.onFormReset still does it explicitly;
         this path only needs to drop the stale results. */
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
